
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, Building, Check } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
interface BuyerResult {
  name: string;
  country?: string;
  count?: number;
}
interface BuyerSearchProps {
  onSelectBuyer: (buyer: string) => void;
}
export const BuyerSearch = ({
  onSelectBuyer
}: BuyerSearchProps) => {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<BuyerResult[]>([]);
  const {
    toast
  } = useToast();
  const {
    t
  } = useTranslation('opportunityFinder');
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setIsLoading(true);
    setResults([]);
    try {
      // For now, we'll simulate results based on the query
      // In production, this should be replaced with a real API call
      setTimeout(() => {
        // Just return the exact search term without variations
        const mockResults = [{
          name: query,
          country: "Denmark"
          // Removed the hardcoded count: 24
        }];
        setResults(mockResults);
        setIsLoading(false);
      }, 1000);

      // Future implementation:
      // const { data, error } = await supabase.functions.invoke('search-buyers', {
      //   body: { query: query.trim() }
      // });
    } catch (error) {
      console.error('Error searching buyers:', error);
      toast({
        title: t("searchFailed"),
        description: t("searchFailedDescription"),
        variant: "destructive"
      });
      setIsLoading(false);
    }
  };
  const handleSelectBuyer = (buyer: string) => {
    onSelectBuyer(buyer);
    toast({
      title: t("buyerSelected"),
      description: t("searchingContracts", {
        buyer
      })
    });
  };
  return <Card>
      <CardHeader>
        <CardTitle>{t("search.title")}</CardTitle>
        <CardDescription>{t("search.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex gap-2">
            <Input placeholder={t("search.placeholder")} value={query} onChange={e => setQuery(e.target.value)} className="flex-1" />
            <Button type="submit" disabled={isLoading || !query.trim()}>
              <Search className="h-4 w-4 mr-2" />
              {t("search.searchButton")}
            </Button>
          </div>
        </form>

        {isLoading && <div className="mt-4 space-y-3">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>}

        {!isLoading && results.length > 0 && <div className="mt-4">
            <h3 className="text-sm font-medium mb-2">
              {t("search.resultsCount", {
            count: results.length
          })}
            </h3>
            <div className="space-y-2">
              {results.map((buyer, index) => <div key={index} className="border p-3 rounded-md hover:bg-muted/50 cursor-pointer flex justify-between" onClick={() => handleSelectBuyer(buyer.name)}>
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-primary/10">
                      <Building className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">{buyer.name}</h4>
                      <div className="text-sm text-muted-foreground">
                        {buyer.country && <span className="mr-2">{t("search.country")}: {buyer.country}</span>}
                        {buyer.count !== undefined && <span>{t("search.contracts")}: {buyer.count}</span>}
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="ml-2">
                    <Check className="h-4 w-4 mr-1" />
                    {t("search.selectButton")}
                  </Button>
                </div>)}
            </div>
          </div>}
      </CardContent>
    </Card>;
};
